import { UnknownError } from '@cian/peperrors';

import { isChatSyncError, ChatSyncError } from '../../errors/ChatSyncError';
import { mapChat } from '../../mappers';
import { TStatus } from '../../repositories/chats/entities/synchronization/V2ScrollSynchronizationRequest';
import {
  TWebChatsSyncRequest,
  TWebChatsSyncResponse,
  fetchWebChatsSync,
} from '../../repositories/chats/v3/web/chats/sync';
import { IApplicationContext } from '../../types/applicationContext';
import { IChat } from '../../types/chatList';
import { EExceptionDomain } from '../../types/exceptionDomains';
import { ETelemetryEvents } from '../../types/telemetry';
import { getTelemetryKeyByPlatformAndHostType } from '../../utils/getTelemetryKeyByPlatformAndHostType';
import { getGenericLikeError } from '../../utils/logging/getGenericLikeError';
import { probeUserAuthentication } from '../probeUserAuthentication';
import {
  EPublicApiEndpoint,
  ERequestSource,
  TRequestDiagnostics,
  TRequestSource,
  getPublicApiLogMeta,
  isMissingUserIdHeaderError,
  trackPublicApiError,
  trackPublicApiSuccess,
} from '../../utils/logging/publicApiDiagnostics';

export type TSyncChatsV2ParamsRequest = Omit<TWebChatsSyncRequest, 'status'> & {
  /** Раздел чатов **/
  status?: TStatus;
};

type TSyncChatsV2Params = {
  parameters: TSyncChatsV2ParamsRequest;
  cookies?: string;
  /** Источник запроса, проставляется на вызывающей стороне */
  source?: TRequestSource;
  /** Условия, в которых выполняется запрос */
  diagnostics?: TRequestDiagnostics;
};

const DEFAULT_ERROR_MESSAGE = 'Ошибка при синхронизации чатов';

interface ISyncChatsResult {
  chats: IChat[];
  movedChatIds: string[];
  error: string | null;
}

export async function syncChatsV2(
  { logger, httpApi, telemetry, config, custom: { realDevice, hostType, subdomain } }: IApplicationContext,
  { cookies, parameters, source = ERequestSource.Unknown, diagnostics }: TSyncChatsV2Params,
): Promise<ISyncChatsResult> {
  const { status } = parameters;

  const options: TWebChatsSyncRequest = {
    ...parameters,
    status: status || 'active',
  };

  const startTime = Date.now();

  const initTelemetryKey = getTelemetryKeyByPlatformAndHostType({
    eventKey: ETelemetryEvents.ChatsSynchronizeInit,
    deviceType: realDevice,
    hostType,
  });

  telemetry.increment(initTelemetryKey);

  /** Код ответа нужен в catch: часть ошибок бросается уже после успешного получения ответа */
  let responseStatusCode: number | undefined;

  try {
    const { response, statusCode }: TWebChatsSyncResponse = await fetchWebChatsSync({
      httpApi,
      parameters: options,
      config: {
        requestConfig: {
          headers: cookies ? [['Cookie', cookies]] : [],
          withCredentials: true,
        },
      },
    });

    responseStatusCode = statusCode;

    if (statusCode === 200) {
      trackPublicApiSuccess();

      telemetry.increment(ETelemetryEvents.ChatsSynchronizeSuccess);
      telemetry.timing(ETelemetryEvents.ChatsSynchronizeTime, Date.now() - startTime);

      return {
        chats: response.chats.map(mapChat),
        movedChatIds: response.movedChatIds || [],
        error: null,
      };
    }

    const error = response.errors?.[0];

    if (statusCode === 400 && error && isChatSyncError(error)) {
      const errorToLog = new ChatSyncError({
        domain: EExceptionDomain.ChatsSync,
        message: error.message,
      });

      logger.error(
        errorToLog,
        getPublicApiLogMeta({
          endpoint: EPublicApiEndpoint.ChatsSync,
          source,
          diagnostics,
          parameters: options,
          statusCode,
          errorIndex: trackPublicApiError(),
        }),
      );

      return {
        chats: [],
        movedChatIds: [],
        error: error.code,
      };
    }

    throw new UnknownError({
      domain: EExceptionDomain.ChatsSync,
      message: response.message || DEFAULT_ERROR_MESSAGE,
      details: {
        errors: response.errors ? JSON.stringify(response.errors) : undefined,
      },
    });
  } catch (error) {
    telemetry.increment(ETelemetryEvents.ChatsSynchronizeFail);

    const errorToLog = getGenericLikeError({
      domain: EExceptionDomain.ChatsSync,
      message: DEFAULT_ERROR_MESSAGE,
      error,
    });

    const probe = isMissingUserIdHeaderError(error)
      ? await probeUserAuthentication({ config, httpApi, logger, telemetry }, subdomain)
      : null;

    logger.error(errorToLog, {
      ...getPublicApiLogMeta({
        endpoint: EPublicApiEndpoint.ChatsSync,
        source,
        diagnostics,
        parameters: options,
        statusCode: responseStatusCode,
        error,
        errorIndex: trackPublicApiError(),
      }),
      probeStatus: probe ? probe.status : null,
      probedUserId: probe ? probe.userId : null,
      probeStatusCode: probe ? probe.statusCode : null,
      probeErrorMessage: probe ? probe.errorMessage : null,
    });

    return {
      chats: [],
      movedChatIds: [],
      error: 'unknownError',
    };
  }
}
